import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import { errorIcon } from '@ld.consumer-portal/data-uri';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

@Component({
  selector: 'ld-document-viewer-modal',
  imports: [BaseModalComponent, MatIconModule, MatProgressSpinnerModule, TranslocoDirective],
  templateUrl: './document-viewer-modal.component.html',
  styleUrl: './document-viewer-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentViewerModalComponent implements OnInit, OnDestroy {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly ngZone = inject(NgZone);

  protected readonly errorIcon = errorIcon;

  readonly glanceIsLoadedFn = input.required<() => boolean>();

  readonly glanceIsInSessionFn = input.required<() => boolean>();

  readonly glanceCanViewPdfFn = input.required<() => boolean>();

  readonly glanceViewPdfFn = input.required<(url: string) => { success: boolean; error?: string }>();

  readonly documentUrl = input.required<string>();

  readonly documentName = input<string>('Document');

  readonly cobrowseUrl = input<string>('');

  readonly close = output<void>();

  readonly pdfShared = output<void>();

  protected readonly isLoading = signal(true);

  protected readonly hasError = signal(false);

  protected readonly isPdfShared = signal(false);

  private glanceCheckIntervalId: ReturnType<typeof setInterval> | null = null;

  private readonly GLANCE_POLL_INTERVAL_MS = 3000;

  protected readonly safeUrl = computed<SafeResourceUrl>(() => {
    const url = this.documentUrl();
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  ngOnInit(): void {
    this.initializeGlanceSessionDetection();
  }

  ngOnDestroy(): void {
    this.stopGlanceSessionPolling();
  }

  private initializeGlanceSessionDetection(): void {
    if (!this.glanceIsLoadedFn()()) {
      return;
    }

    if (this.glanceIsInSessionFn()()) {
      this.sharePdfWithAgent();
    } else {
      this.startGlanceSessionPolling();
    }
  }

  private startGlanceSessionPolling(): void {
    this.stopGlanceSessionPolling();

    this.ngZone.runOutsideAngular(() => {
      this.glanceCheckIntervalId = setInterval(() => {
        if (this.glanceIsLoadedFn()() && this.glanceIsInSessionFn()() && this.glanceCanViewPdfFn()()) {
          this.ngZone.run(() => {
            this.stopGlanceSessionPolling();
            this.sharePdfWithAgent();
          });
        }
      }, this.GLANCE_POLL_INTERVAL_MS);
    });
  }

  private stopGlanceSessionPolling(): void {
    if (this.glanceCheckIntervalId !== null) {
      clearInterval(this.glanceCheckIntervalId);
      this.glanceCheckIntervalId = null;
    }
  }

  private sharePdfWithAgent(): void {
    if (this.isPdfShared()) {
      return;
    }

    const urlToShare = this.cobrowseUrl() || this.documentUrl();
    if (!urlToShare) {
      return;
    }

    const result = this.glanceViewPdfFn()(urlToShare);
    if (result.success) {
      this.isPdfShared.set(true);
      this.pdfShared.emit();
    }
  }

  protected onIframeLoad(): void {
    this.isLoading.set(false);
  }

  protected onIframeError(): void {
    this.isLoading.set(false);
    this.hasError.set(true);
  }

  protected onClose(): void {
    this.stopGlanceSessionPolling();
    this.close.emit();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.stopGlanceSessionPolling();
      this.close.emit();
    }
  }
}
